import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  Camera,
  Loader2,
  Lock,
  Pencil,
  Save,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ContainerOrderLine } from "../../types/containerOrders";
import {
  cargoIssueOf,
  deriveVgm,
  effectiveCargoKg,
  evaluateWeights,
  issueFor,
} from "../../utils/containerLineCargo";
import {
  checkContainerNumber,
  containerNumberMessage,
} from "../../utils/containerNumber";

/**
 * The box's own figures, as one panel with three states.
 *
 * This is where the redesign actually bites, and the change is not cosmetic:
 * these fields used to be demanded *before* the driver's first step. On the
 * commonest route of all that meant asking for a container number, a plated
 * tare and a payload rating while the driver was still in the cab on the way to
 * a terminal to collect a box nobody had seen. The number is on a door; the
 * tare and the rating are on the plate beside it. The only way to satisfy the
 * old gate was for somebody to guess, and somebody did.
 *
 * So the panel is locked until the driver reports in, and then it is the job:
 *
 *   locked   before arrival. Every row is drawn, greyed, with no value — so the
 *            driver can see exactly what will be asked of them without being
 *            asked for it yet. A hidden panel would be a surprise at the gate.
 *   reading  after arrival. The figures as they stand, with two ways to change
 *            them: photograph the door, or type.
 *   editing  the same rows as inputs.
 *
 * The VGM is on none of those lists as an input, in any state. It is derived —
 * tare plus what the packing list weighs, which is SOLAS Method 2 — and the
 * server computes the identical sum on save and ignores anything a client sends
 * for it. It is shown with its arithmetic beside it, because a figure nobody
 * can edit has to show its working or the only way to change it is to guess
 * which field above it came from.
 */

interface DriverContainerDetailsProps {
  line: ContainerOrderLine;
  /** False before the driver has reported in at this stop. */
  unlocked: boolean;
  /** The container is finished or cancelled — a record, not a draft. */
  readOnly: boolean;
  /** The container number is settled once the run has begun. */
  numberFrozen: boolean;
  busy: boolean;
  /**
   * Whether the read view carries the customs MRN itself.
   *
   * False inside the stop frame, which draws the MRN as a fact of its own
   * directly above this panel — a second copy there is the same number twice on
   * a phone screen. True when the panel stands on its own, after the run has
   * finished and there is no frame left to carry it, where the "duplicate"
   * is the only copy there is.
   */
  showMrn?: boolean;
  /** Which of these fields the run is currently waiting on, as field keys. */
  missing: readonly string[];
  onSave: (values: {
    containerNumber: string;
    sealNumber: string;
    mrnNumber: string;
    tareKg: string;
    maxPayloadKg: string;
    cargoWeightKg: string;
  }) => Promise<boolean>;
  onScanPlate: () => void;
}

/** An empty box means "not stated", which is null — not zero. */
const numOrNull = (value: string) =>
  value.trim() === "" || Number.isNaN(Number(value)) ? null : Number(value);

const kg = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : `${value.toLocaleString()} kg`;

export function DriverContainerDetails({
  line,
  unlocked,
  readOnly,
  numberFrozen,
  busy,
  showMrn = false,
  missing,
  onSave,
  onScanPlate,
}: DriverContainerDetailsProps) {
  const { t } = useTranslation("containerOrders");

  const [editing, setEditing] = useState(false);
  const [containerNumber, setContainerNumber] = useState("");
  const [sealNumber, setSealNumber] = useState("");
  const [mrnNumber, setMrnNumber] = useState("");
  const [tareKg, setTareKg] = useState("");
  const [maxPayloadKg, setMaxPayloadKg] = useState("");
  const [cargoWeightKg, setCargoWeightKg] = useState("");

  /**
   * Put the boxes back to what is actually stored on the container.
   *
   * Called from three places, and the third is the one that was missing: on
   * mount, whenever the stored figures move under the panel, and on Cancel.
   *
   * Cancel used to only set `editing` to false. The typed values stayed in
   * state, so reopening the form showed the numbers somebody had just abandoned
   * — sitting in the boxes looking exactly like the container's own, and one
   * tap from being saved. A cancel that keeps what it cancelled is not a cancel;
   * it is a hidden draft, and the whole reason a driver presses it is that the
   * values are wrong.
   */
  const reseed = useCallback(() => {
    setContainerNumber(line.containerNumber ?? "");
    setSealNumber(line.sealNumber ?? "");
    setMrnNumber(line.mrnNumber ?? "");
    setTareKg(line.tareKg != null ? String(line.tareKg) : "");
    setMaxPayloadKg(line.maxPayloadKg != null ? String(line.maxPayloadKg) : "");
    setCargoWeightKg(
      line.cargoWeightKg != null ? String(line.cargoWeightKg) : "",
    );
  }, [
    line.containerNumber,
    line.sealNumber,
    line.mrnNumber,
    line.tareKg,
    line.maxPayloadKg,
    line.cargoWeightKg,
  ]);

  /**
   * Re-seed whenever the stored container changes — but never under a driver
   * who is typing.
   *
   * The re-seed exists for a plate reading landing: the reader saves through
   * the same endpoint the driver's own edit does, so the container comes back
   * with new figures on it, and boxes still holding the old ones would quietly
   * undo the import on the next save.
   *
   * The guard exists because `updatedAt` moves for reasons that are nothing to
   * do with these figures. Filing a document is a save, and the documents are
   * inside the same frame as this panel — so a driver who opened the edit form,
   * typed a tare off the plate and then photographed the gate receipt had the
   * tare wiped from under them by the upload's new version token. Anything that
   * genuinely needs these boxes repopulated closes the form first: Cancel and
   * Edit both call `reseed` by hand, and a successful save leaves the boxes
   * holding exactly what was saved.
   */
  useEffect(() => {
    if (!editing) reseed();
    // `editing` is deliberately not a dependency: leaving the form is already
    // handled by Cancel and by the save, and re-running on the transition would
    // re-seed at the one moment the values must be left alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reseed, line.updatedAt]);

  /**
   * A container that goes read-only under an open form closes it.
   *
   * The two are on screen together: the driver can be editing these figures and
   * confirm the last step of the run in the same frame, and a form left open
   * over a finished container offers a Save the API will refuse — with the
   * driver's own typing in it, looking like work that is one tap from being
   * kept. What it can be is a record, which is what the read view is.
   */
  useEffect(() => {
    if (readOnly && editing) {
      reseed();
      setEditing(false);
    }
  }, [readOnly, editing, reseed]);

  /** Leave the form without keeping what was typed into it. */
  const cancelEdit = () => {
    reseed();
    setEditing(false);
  };

  /**
   * What the packing list itemises — dispatch's figure, and not typed here.
   *
   * It is on this panel because it is one of the two halves of the VGM below
   * it. A screen that shows a gross mass while showing nothing of what it has
   * to cover is showing a number nobody can check.
   */
  const listedCargoKg = (line.cargoItems ?? []).reduce(
    (total, item) => total + (item.weightKg ?? 0),
    0,
  );

  /**
   * What the load was weighed at — and this screen is where that figure lands.
   *
   * The weighing happens after loading, at stop 2 of 3, and the person handed
   * the bridge ticket is the driver. Dispatch can enter it too, but only by
   * being told; this is the one screen standing where the number is produced.
   *
   * Read off the box while editing so the VGM below moves as it is typed, and
   * off the container otherwise. Empty means "not weighed" and falls back to
   * the rows; a typed `0` is a real declaration about an empty move.
   */
  const liveDeclaredCargo = editing
    ? numOrNull(cargoWeightKg)
    : (line.cargoWeightKg ?? null);
  const cargoKg = effectiveCargoKg(liveDeclaredCargo, listedCargoKg);

  // The sum, off the boxes being typed into rather than off the stored
  // container, so it moves as the tare is corrected.
  const liveTare = editing ? numOrNull(tareKg) : (line.tareKg ?? null);
  const derivedVgm = deriveVgm({
    tareKg: liveTare,
    declaredCargoKg: liveDeclaredCargo,
    listedKg: listedCargoKg,
  });

  /**
   * How the weights stand, from the same function the office page uses and the
   * mirror of the one the API enforces — so a set this panel accepts is never
   * one the save then refuses.
   */
  const weights = evaluateWeights({
    tareKg: liveTare,
    maxPayloadKg: editing
      ? numOrNull(maxPayloadKg)
      : (line.maxPayloadKg ?? null),
    vgmKg: derivedVgm,
    // Both figures, so the rules can compare them and report against whichever
    // box the driver can actually do something about.
    cargoKg: listedCargoKg,
    declaredCargoKg: liveDeclaredCargo,
  });
  const plateIssue = issueFor(weights, "maxPayloadKg");
  /**
   * Whatever is wrong with the load, for marking the row that shows it.
   *
   * Still the either-or reader: the cargo row on this panel shows whichever
   * figure is in force, so it has to turn red for a fault in either of them.
   * What is *reported in words* below is narrower — see the note there.
   */
  const cargoIssue = cargoIssueOf(weights);
  /** The declared load specifically — the one box on this panel. */
  const declaredIssue = issueFor(weights, "cargoWeightKg");

  /**
   * How the number reads against ISO 6346 — advisory, exactly as on the
   * office's form. What is painted on the container is the truth even when the
   * arithmetic disagrees, so this is a prompt to look again, not a gate.
   */
  const numberCheck = checkContainerNumber(containerNumber);
  const numberProblem = containerNumberMessage(numberCheck);

  const owed = (key: string) => missing.includes(key);

  const save = async () => {
    const saved = await onSave({
      containerNumber,
      sealNumber,
      mrnNumber,
      tareKg,
      maxPayloadKg,
      cargoWeightKg,
    });
    if (saved) setEditing(false);
  };

  // ── Locked ────────────────────────────────────────────────────────────────
  //
  // Drawn in full, valueless. The driver sees the shape of what is coming
  // without being asked for it, which is the difference between a panel that
  // unlocks and a panel that appears.
  if (!unlocked) {
    return (
      <div className="rounded-[10px] bg-white p-3 opacity-60">
        <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-[#A6A49C]">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          {t("driver.detailsAfterArrival")}
        </p>
        <dl className="space-y-1.5">
          {[
            "containerNumber",
            "sealNumber",
            // No MRN. It is a row in the stop frame directly above this panel,
            // drawn at every stop and drawn even when it is blank, so a second
            // copy here is the same fact twice on a phone screen. It comes back
            // in the editing state below, where it is not a duplicate but the
            // one place the number can be typed.
            "tare",
            "maxPayload",
            // No packages row either, and for the same reason as the MRN: the
            // packing list is its own panel directly beneath this one now, as a
            // table the driver can edit. A greyed "Packages —" above a table
            // that lists them is the same question asked twice.
            "cargoWeight",
            "vgm",
          ].map((key) => (
            <div
              key={key}
              className="flex items-baseline justify-between gap-3 rounded-lg bg-[#F7F6F2] px-2 py-1.5"
            >
              <dt className="text-[12.5px] text-[#6B6A64]">
                {t(`driver.${key}`)}
              </dt>
              <dd className="text-[13px] text-[#C9C7BE]">—</dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  // ── Unlocked ──────────────────────────────────────────────────────────────
  return (
    <div className="rounded-[10px] bg-white p-3">
      {/* Two buttons, and only two. Photographing the door is the faster way to
          fill this in, so it leads and is the solid one; typing is the fallback
          behind it. Both disappear on a finished container, where these figures
          are a record of what was handed over rather than a draft. */}
      {!readOnly && !editing && (
        <div className="mb-3 flex gap-2">
          <button
            type="button"
            onClick={onScanPlate}
            disabled={busy}
            className="flex flex-1 items-center justify-center gap-[7px] rounded-[24px] bg-[#BA7517] px-3 py-3 text-[13px] font-semibold text-white disabled:opacity-50"
          >
            <Camera className="h-4 w-4" />
            {t("driver.doorPhoto")}
          </button>
          <button
            type="button"
            onClick={() => {
              // Always open on the container's own figures. Cancel already
              // restores them, and so does this — a save that failed leaves
              // rejected values in state, and they must not be what the next
              // edit starts from.
              reseed();
              setEditing(true);
            }}
            disabled={busy}
            className="flex shrink-0 items-center justify-center gap-1.5 rounded-[24px] border border-[#EF9F27] bg-[#F7F6F2] px-4 py-3 text-[13px] font-semibold text-[#854F0B] disabled:opacity-50"
          >
            <Pencil className="h-4 w-4" />
            {t("driver.edit")}
          </button>
        </div>
      )}

      {editing ? (
        <div className="space-y-2.5">
          {/* The box's two identifiers. The number is frozen once the run has
              begun — the terminal paperwork was filed against it — and the seal
              never is: it is fitted when the container is closed at the loading
              site, so at the only moment the number exists, the run has already
              started and the driver is the person looking at it. */}
          <Field
            label={t("driver.containerNumber")}
            required={owed("containerNumber")}
            /**
             * Only spoken to when something is wrong.
             *
             * There was a green "looks good — this is a valid container
             * number" under this box, and it was noise: a driver who has typed
             * eleven characters correctly does not need to be congratulated,
             * and a screen that comments on every field teaches people to stop
             * reading it — which is precisely the habit that makes the *warning*
             * on this same box get skipped. Silence is the pass.
             */
            error={
              numberProblem ? t(numberProblem.key, numberProblem.values) : null
            }
          >
            <input
              value={containerNumber}
              onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
              disabled={numberFrozen}
              className={`h-10 w-full rounded-lg border px-3 font-mono text-sm uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-[#EF9F27] disabled:bg-[#F7F6F2] disabled:text-[#6B6A64] ${
                numberProblem ? "border-[#A63A39]" : "border-gray-300"
              }`}
            />
          </Field>

          <Field label={t("driver.sealNumber")}>
            <input
              value={sealNumber}
              onChange={(e) => setSealNumber(e.target.value.toUpperCase())}
              className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#EF9F27]"
            />
          </Field>

          {/* ── The customs MRN ──────────────────────────────────────────
              Editable here for the whole run, and it is the only identifier on
              this panel that never freezes. A declaration is routinely issued —
              or amended, or reissued — at a customs office partway along the
              route, and that office is a place only the driver is standing. A
              field locked with the container number would be locked one stop
              before the leg on which its value is usually obtained.

              No format check, on purpose. An MRN's shape varies by member state
              and by year, and a validator that has not been taught this year's
              pattern would refuse a valid number at a barrier with the paper
              already in the driver's hand. What is printed is the truth; this
              box carries it. */}
          <Field label={t("driver.mrnNumber")}>
            <input
              value={mrnNumber}
              onChange={(e) => setMrnNumber(e.target.value.toUpperCase())}
              inputMode="text"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              className="h-10 w-full rounded-lg border border-gray-300 px-3 font-mono text-sm uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-[#EF9F27]"
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label={t("driver.tare")} required={owed("tareKg")}>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={tareKg}
                onChange={(e) => setTareKg(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#EF9F27]"
              />
            </Field>
            <Field label={t("driver.maxPayload")} required={owed("maxPayloadKg")}>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={maxPayloadKg}
                onChange={(e) => setMaxPayloadKg(e.target.value)}
                className={`h-10 w-full rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#EF9F27] ${
                  plateIssue ? "border-[#A63A39]" : "border-gray-300"
                }`}
              />
            </Field>
          </div>

          {/* Said once, under the pair, because the rule is about the two of
              them together and the message names both. */}
          {plateIssue && (
            <p className="flex items-start gap-1.5 text-[11px] text-[#A63A39]">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
              {t(`lineWeights.${plateIssue.code}`, plateIssue.values)}
            </p>
          )}

          {/* The two derived rows, shown while editing so the sum can be
              watched as the tare is corrected. Neither is an input. */}
          {/* ── What the load weighs ──────────────────────────────────────
              An input now, and this screen is where it belongs: the box is
              weighed after loading, and the driver is the one handed the
              ticket. It used to be a read-only echo of dispatch's packing list,
              which meant the person holding the actual figure had nowhere to
              put it.

              Empty is not zero. Blank hands the answer back to the packing list
              — the placeholder shows what that comes to — while a typed `0` is
              a declaration that the box is going out empty. */}
          <Field label={t("driver.cargoWeight")}>
            <input
              type="number"
              min={0}
              inputMode="numeric"
              value={cargoWeightKg}
              onChange={(e) => setCargoWeightKg(e.target.value)}
              placeholder={String(Math.round(listedCargoKg))}
              className={`h-10 w-full rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#EF9F27] ${
                cargoIssue ? "border-[#A63A39]" : "border-gray-300"
              }`}
            />
          </Field>

          {/* What dispatch itemised, as a count. Shown while editing because it
              is what the box above has to cover, and because a count is not a
              second opinion about a weight — there is nothing here to mistake
              for the figure being typed.

              The listed *weight* used to sit here too and has been taken out.
              Two kilogram figures a centimetre apart, one editable and one not,
              disagreeing whenever a load was weighed at anything other than
              what the paperwork said — which is most of the time — is a screen
              asking the driver to work out which of the two it means. The
              listed figure has not gone anywhere: it is the placeholder in the
              box above, where it reads as what blank falls back to, and it is
              the whole of the row when nothing has been weighed. */}
          {/* The package count has left this form as well. It was a readout
              beside the weight boxes because the packing list was somebody
              else's and this was the only place a driver could see it; it is
              now a table of its own directly beneath this panel, which the
              driver can edit. Keeping the readout would be showing the same
              count twice on one screen, once uneditable, which is exactly the
              confusion the listed-weight row was removed to avoid. */}
          <Readout
            label={t("driver.vgm")}
            value={kg(derivedVgm)}
            note={
              derivedVgm === null
                ? t("lineEdit.hints.vgmNeedsTare")
                : t("lineEdit.hints.vgmSum", {
                    tare: (liveTare ?? 0).toLocaleString(),
                    cargo: Math.round(cargoKg).toLocaleString(),
                  })
            }
            strong
          />

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={busy}
              className="flex shrink-0 items-center justify-center gap-1.5 rounded-[24px] border border-[#C9C7BE] bg-[#F7F6F2] px-4 py-3 text-[13px] font-semibold text-[#6B6A64]"
            >
              <X className="h-4 w-4" />
              {t("lineEdit.cancel")}
            </button>
            <button
              type="button"
              onClick={save}
              // Greyed for the one reason the save is actually refused: weights
              // that contradict each other. A container number that fails its
              // check digit is *not* blocked — a box in a yard occasionally
              // carries a mis-stencilled number and the driver still has to
              // record what is painted on it.
              disabled={busy || weights.errors.length > 0}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-[24px] bg-[#BA7517] px-4 py-3 text-[13px] font-semibold text-white disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {t("driver.saveDetails")}
            </button>
          </div>
        </div>
      ) : (
        <dl className="space-y-1.5">
          {[
            {
              key: "containerNumber",
              label: t("driver.containerNumber"),
              value: line.containerNumber,
              mono: true,
              owed: owed("containerNumber"),
            },
            {
              key: "sealNumber",
              label: t("driver.sealNumber"),
              value: line.sealNumber,
              mono: true,
            },
            // The MRN only when nothing above is carrying it — see `showMrn`.
            // Editing always puts the field back, because that is where the
            // number is entered rather than merely read.
            ...(showMrn
              ? [
                  {
                    key: "mrnNumber",
                    label: t("driver.mrnNumber"),
                    value: line.mrnNumber,
                    mono: true,
                  },
                ]
              : []),
            {
              key: "tare",
              label: t("driver.tare"),
              value: kg(line.tareKg),
              owed: owed("tareKg"),
            },
            {
              key: "maxPayload",
              label: t("driver.maxPayload"),
              value: kg(line.maxPayloadKg),
              owed: owed("maxPayloadKg"),
            },
            {
              key: "cargoWeight",
              // Named for where the figure came from, because the remedy
              // differs: a weighed load is corrected here, a listed one is
              // dispatch's to correct. A single label for both would leave the
              // driver unable to tell which they are looking at.
              label: t(
                line.cargoWeightKg != null
                  ? "driver.cargoWeight"
                  : "driver.listedWeight",
              ),
              value: kg(cargoKg),
              owed: owed("cargo"),
              problem: !!cargoIssue,
            },
            {
              key: "vgm",
              label: t("driver.vgm"),
              value: kg(derivedVgm),
              strong: true,
            },
          ].map(({ key, label, value, mono, owed: isOwed, strong, problem }) => (
            <div
              key={key}
              className="flex items-baseline justify-between gap-3 rounded-lg bg-[#F7F6F2] px-2 py-1.5"
            >
              <dt className="shrink-0 text-[12.5px] text-[#6B6A64]">
                {label}
                {/* What the run is waiting on, marked where it is entered
                    rather than only in a banner somewhere above. */}
                {isOwed && <span className="ml-1 text-[#A63A39]">*</span>}
              </dt>
              <dd
                className={`min-w-0 break-words text-right text-[13px] ${
                  mono ? "font-mono" : ""
                } ${
                  problem
                    ? "font-semibold text-[#A63A39]"
                    : strong
                      ? "font-bold text-[#1A1A18]"
                      : "font-medium text-[#1A1A18]"
                }`}
              >
                {String(value ?? "").trim() || "—"}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* The *declared* load against what the box allows — this panel's own
          box, and only that one.
          A problem with the packing list is reported by the cargo panel below
          instead, where the rows are. Both used to be reported here, because
          the rows were dispatch's and this was the only screen that could say
          anything about them at all; now that the driver can take a pallet off
          the list, the complaint belongs beside the list. Reporting both in
          both places would be the same overload said twice, with the driver
          left to work out which of the two numbers to move. */}
      {declaredIssue && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-[#FBEDED] px-2.5 py-2 text-[11px] text-[#A63A39]">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {t(`lineWeights.${declaredIssue.code}`, declaredIssue.values)}
        </p>
      )}
    </div>
  );
}

/** One labelled input, with whatever the field currently has to say. */
function Field({
  label,
  required = false,
  error = null,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-[#6B6A64]">
        {label}
        {required && <span className="ml-1 text-[#A63A39]">*</span>}
      </span>
      {children}
      {/* Nothing is said when the field is fine. A screen that comments on
          every correct entry trains people to stop reading it, which is exactly
          the habit that makes the one real warning get skipped. */}
      {error && (
        <span className="mt-1 flex items-start gap-1.5 text-[11px] text-[#A63A39]">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {error}
        </span>
      )}
    </label>
  );
}

/** A figure the driver cannot type, with its arithmetic beside it. */
function Readout({
  label,
  value,
  note,
  strong = false,
}: {
  label: string;
  value: string;
  note: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 rounded-lg bg-[#FAEEDA] px-3 py-2">
      <span className="shrink-0 text-xs font-medium text-[#6B6A64]">
        {label}
      </span>
      <span className="min-w-0 text-right">
        <span
          className={`block text-sm ${
            strong ? "font-bold text-[#1A1A18]" : "font-medium text-[#1A1A18]"
          }`}
        >
          {value}
        </span>
        <span className="block text-[10px] text-[#A6A49C]">{note}</span>
      </span>
    </div>
  );
}

export default DriverContainerDetails;
